import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import type { UserSettings } from '@/types';

const DEFAULT_SETTINGS: UserSettings = {
  currency: 'USD',
  displayCurrency: undefined,
  language: 'en',
  theme: 'system',
  yearlyGoal: 0,
  defaultMonthlySalary: 0,
};

export async function getUserSettings(userId: string): Promise<UserSettings> {
  try {
    const settingsDoc = doc(db, `users/${userId}/settings/profile`);
    const settingsSnap = await getDoc(settingsDoc);

    if (settingsSnap.exists()) {
      const data = settingsSnap.data() as Partial<UserSettings>;
      return {
        ...DEFAULT_SETTINGS,
        ...data,
        defaultMonthlySalary: data.defaultMonthlySalary ?? 0,
        displayCurrency: data.displayCurrency ?? undefined,
      };
    }

    await setDoc(settingsDoc, cleanForFirestore(DEFAULT_SETTINGS));
    return DEFAULT_SETTINGS;
  } catch (error) {
    console.error('Error getting user settings:', error);
    return DEFAULT_SETTINGS;
  }
}

export async function updateUserSettings(
  userId: string,
  settings: Partial<UserSettings>
): Promise<void> {
  try {
    const settingsDoc = doc(db, `users/${userId}/settings/profile`);
    const currentSettings = await getUserSettings(userId);
    const merged = { ...currentSettings, ...settings };
    await setDoc(settingsDoc, cleanForFirestore(merged));
  } catch (error) {
    console.error('Error updating user settings:', error);
    throw error;
  }
}

// Firestore does not accept `undefined` values — remove them before saving
function cleanForFirestore(settings: UserSettings): Partial<UserSettings> {
  const result: Partial<UserSettings> = {};
  (Object.keys(settings) as Array<keyof UserSettings>).forEach((key) => {
    if (settings[key] !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (result as any)[key] = settings[key];
    }
  });
  return result;
}